/**
 * Обслуживание приложения на устройстве: проверка обновлений и полный сброс.
 * Обе кнопки живут в настройках, в разделе «Поддержка».
 *
 * Функции обычные, а не методы миксина: компоненту от них нужен только
 * результат, а сброс ещё и уводит со страницы — держать под него состояние
 * компонента незачем. Отписка от push в сброс не входит, она требует
 * запроса к бэку и живёт в methodsPush; вызывающий делает её до сброса.
 *
 * Серверная половина — заголовки кеширования и адрес /reset — описана в
 * docs/cache-and-reset-backend.md.
 */

/** Сколько ждать загрузки найденной версии, прежде чем сказать «загружается». */
const INSTALL_TIMEOUT = 60 * 1000;

/**
 * Возврат в приложение проверяет обновление не чаще этого. Установленное PWA
 * почти не перезапускается, а только выходит из фона, и сам браузер при этом
 * новую версию не ищет — отсюда и «залипание» на старой.
 */
const QUIET_CHECK_INTERVAL = 10 * 60 * 1000;

let lastQuietCheck = 0;

function currentRegistration() {
  if (!('serviceWorker' in navigator)) return Promise.resolve(null);

  return navigator.serviceWorker.getRegistration()
    .then((registration) => registration || null)
    .catch(() => null);
}

/**
 * Ждёт, пока найденная версия докачается: installed — готова и ждёт
 * разрешения, redundant — установка сорвалась. Отдаёт false, если не
 * дождались: версия есть, но ещё грузится.
 */
function waitForInstall(worker) {
  if (!worker) return Promise.resolve(true);

  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(false), INSTALL_TIMEOUT);
    const onStateChange = () => {
      if (worker.state === 'installing') return;

      clearTimeout(timer);
      worker.removeEventListener('statechange', onStateChange);
      resolve(true);
    };

    worker.addEventListener('statechange', onStateChange);
    onStateChange();
  });
}

/**
 * Проверка по нажатию. Отдаёт { status, registration }:
 *
 * available   — новая версия скачана и ждёт; применяется через applyUpdate.
 * downloading — новая версия найдена, но ещё грузится. Когда догрузится,
 *               сработает обычное предложение обновиться из меню.
 * latest      — стоит последняя версия.
 * unsupported — service worker нет: браузер не умеет или это dev-сервер.
 * failed      — проверить не удалось, чаще всего нет сети.
 */
export function checkForUpdate() {
  return currentRegistration()
    .then((registration) => {
      if (!registration) return { status: 'unsupported' };
      // Прошлая проверка уже скачала версию, а человек от неё отказался.
      if (registration.waiting) return { status: 'available', registration };

      return registration.update()
        .then(() => waitForInstall(registration.installing))
        .then((settled) => {
          if (!settled) return { status: 'downloading' };

          return registration.waiting
            ? { status: 'available', registration }
            : { status: 'latest' };
        });
    })
    .catch(() => ({ status: 'failed' }));
}

/**
 * Включает скачанную версию. Перезагрузку делает обработчик controllerchange
 * в methodsPwaUpdate — тот же, что и у предложения обновиться в меню.
 */
export function applyUpdate(registration) {
  if (registration?.waiting) registration.waiting.postMessage({ type: 'SKIP_WAITING' });
}

/**
 * Тихая проверка при возврате в приложение. Ничего не показывает сама:
 * найденную версию подхватит register-service-worker и поднимет обычное
 * предложение обновиться.
 */
export function checkForUpdateQuietly() {
  const now = Date.now();
  if (now - lastQuietCheck < QUIET_CHECK_INTERVAL) return Promise.resolve();

  lastQuietCheck = now;

  return currentRegistration()
    .then((registration) => registration && registration.update())
    .catch(() => {});
}

function unregisterServiceWorkers() {
  if (!('serviceWorker' in navigator)) return Promise.resolve();

  return navigator.serviceWorker.getRegistrations()
    .then((registrations) => Promise.all(registrations.map((item) => item.unregister())));
}

function deleteCaches() {
  if (!('caches' in window)) return Promise.resolve();

  return caches.keys()
    .then((keys) => Promise.all(keys.map((key) => caches.delete(key))));
}

/**
 * Сами приложение IndexedDB не пользуется, но сброс обещает стереть всё.
 * databases() есть не везде — где его нет, шаг пропускаем: имён баз не узнать.
 */
function deleteDatabases() {
  if (!window.indexedDB?.databases) return Promise.resolve();

  return window.indexedDB.databases()
    .then((list) => list.forEach(({ name }) => {
      if (name) window.indexedDB.deleteDatabase(name);
    }));
}

/**
 * Полный сброс: service worker, его кеши, хранилища, и перезапуск с сервера.
 *
 * Service worker снимаем первым: пока он жив, он может положить в кеш то,
 * что мы только что стёрли. Снятый продолжает обслуживать эту страницу, но
 * следующая загрузка идёт уже мимо него.
 *
 * Каждый шаг проглатывает свою ошибку: недоделанный сброс лучше, чем
 * никакого, а перезапуск нужен при любом исходе.
 *
 * Параметр в адресе обходит HTTP-кеш браузера: его из JS не стереть, и без
 * параметра index.html может прийти оттуда же, откуда пришёл старый.
 *
 * Работает только в сети: после сброса приложению не из чего загрузиться.
 * Проверять это — забота вызывающего.
 */
export function hardReset() {
  const step = (action) => () => action().catch(() => {});

  return Promise.resolve()
    .then(step(unregisterServiceWorkers))
    .then(step(deleteCaches))
    .then(step(deleteDatabases))
    .then(() => {
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch (error) {
        // Хранилище закрыто настройками браузера — стирать в нём нечего.
      }

      window.location.replace(`${process.env.BASE_URL}?reset=${Date.now()}`);
    });
}
